import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../context/AuthContext";
import { departmentAPI } from "../services/api";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    fname: "",
    sname: "",
    dpt_id: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await departmentAPI.getAll();
        setDepartments(response.data.data);
      } catch (error) {
        console.error("Failed to fetch departments:", error);
      }
    };
    fetchDepartments();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: "",
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = "กรุณากรอกอีเมล์";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "กรุณากรอกอีเมล์ให้ถูกต้อง";
    }

    if (!formData.password) {
      newErrors.password = "กรุณากรอกรหัสผ่าน";
    } else if (formData.password.length < 6) {
      newErrors.password = "รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "รหัสผ่านไม่ตรงกัน";
    }

    if (!formData.fname) {
      newErrors.fname = "กรุณากรอกชื่อ";
    }

    if (!formData.sname) {
      newErrors.sname = "กรุณากรอกนามสกุล";
    }

    if (!formData.dpt_id) {
      newErrors.dpt_id = "กรุณาเลือกหน่วยงาน";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const success = await register({
        email: formData.email,
        password: formData.password,
        fname: formData.fname,
        sname: formData.sname,
        dpt_id: formData.dpt_id,
      });

      if (success) {
        navigate("/login");
      }
    } catch (error) {
      console.error("Registration error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>ลงทะเบียน | งานบริการวิชาการ มหาวิทยาลัยราชภัฏศรีสะเกษ</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50 py-12 flex items-center justify-center">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-block p-4 bg-primary-100 rounded-full mb-4">
                  <img
                    src="/assets/dist/img/AdminLTELogo.png"
                    alt="SSKRU Logo"
                    className="w-20 h-20 object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src =
                        "https://via.placeholder.com/80x80?text=SSKRU";
                    }}
                  />
                </div>
                <h2 className="text-3xl font-bold text-primary-500 mb-2">
                  ลงทะเบียน
                </h2>
                <p className="text-gray-600">
                  สร้างบัญชีผู้ใช้ใหม่สำหรับงานบริการวิชาการ
                </p>
              </div>

              {/* Register Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Fields - 2 columns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* First Name */}
                  <div>
                    <label
                      htmlFor="fname"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      <i className="fas fa-user text-primary-500 mr-2"></i>
                      ชื่อ
                    </label>
                    <input
                      type="text"
                      id="fname"
                      name="fname"
                      value={formData.fname}
                      onChange={handleChange}
                      placeholder="ชื่อ"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all ${
                        errors.fname ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {errors.fname && (
                      <p className="mt-1 text-sm text-red-500 flex items-center">
                        <i className="fas fa-exclamation-circle mr-1"></i>
                        {errors.fname}
                      </p>
                    )}
                  </div>

                  {/* Last Name */}
                  <div>
                    <label
                      htmlFor="sname"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      <i className="fas fa-user text-primary-500 mr-2"></i>
                      นามสกุล
                    </label>
                    <input
                      type="text"
                      id="sname"
                      name="sname"
                      value={formData.sname}
                      onChange={handleChange}
                      placeholder="นามสกุล"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all ${
                        errors.sname ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {errors.sname && (
                      <p className="mt-1 text-sm text-red-500 flex items-center">
                        <i className="fas fa-exclamation-circle mr-1"></i>
                        {errors.sname}
                      </p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    <i className="fas fa-envelope text-primary-500 mr-2"></i>
                    อีเมล์
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="example@sskru.ac.th"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-500 flex items-center">
                      <i className="fas fa-exclamation-circle mr-1"></i>
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Department */}
                <div>
                  <label
                    htmlFor="dpt_id"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    <i className="fas fa-building text-primary-500 mr-2"></i>
                    หน่วยงาน
                  </label>
                  <select
                    id="dpt_id"
                    name="dpt_id"
                    value={formData.dpt_id}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all ${
                      errors.dpt_id ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <option value="">เลือกหน่วยงาน</option>
                    {departments.map((dept) => (
                      <option key={dept.dpt_id} value={dept.dpt_id}>
                        {dept.dpt_name}
                      </option>
                    ))}
                  </select>
                  {errors.dpt_id && (
                    <p className="mt-1 text-sm text-red-500 flex items-center">
                      <i className="fas fa-exclamation-circle mr-1"></i>
                      {errors.dpt_id}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    <i className="fas fa-lock text-primary-500 mr-2"></i>
                    รหัสผ่าน
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="รหัสผ่าน"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all pr-12 ${
                        errors.password ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      <i
                        className={`fas fa-${showPassword ? "eye-slash" : "eye"}`}
                      ></i>
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-500 flex items-center">
                      <i className="fas fa-exclamation-circle mr-1"></i>
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    <i className="fas fa-lock text-primary-500 mr-2"></i>
                    ยืนยันรหัสผ่าน
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="ยืนยันรหัสผ่าน"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all pr-12 ${
                        errors.confirmPassword
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      <i
                        className={`fas fa-${showConfirmPassword ? "eye-slash" : "eye"}`}
                      ></i>
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-500 flex items-center">
                      <i className="fas fa-exclamation-circle mr-1"></i>
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                {/* Password Requirements */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800 flex items-start gap-2">
                    <i className="fas fa-info-circle mt-0.5"></i>
                    <span>รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร</span>
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary-500 text-white py-3 rounded-lg hover:bg-primary-600 transition-all hover:shadow-lg transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none font-semibold flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      กำลังลงทะเบียน...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-user-plus"></i>
                      ลงทะเบียน
                    </>
                  )}
                </button>
              </form>

              {/* Login Link */}
              <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                <p className="text-gray-600">
                  มีบัญชีผู้ใช้แล้ว?{" "}
                  <Link
                    to="/login"
                    className="text-primary-500 font-semibold hover:text-primary-600 hover:underline"
                  >
                    เข้าสู่ระบบ
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegisterPage;
